import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import '../shared/HubLayout.css';
import { useMobileSubNav } from '../layout/MobileNavContext';
import { useChildNav } from '../layout/useChildNav';
import { useHubScreen } from '../layout/useHubScreen';
import { Fetch } from '../utils/Utils.js';
import { LISTS_TAB, parseListsPath, listsPathFor } from './listsPath';
import { formatListCode } from './listCode';
import { ListButton, AddButton, FAB } from '../shared/ActionButtons';
import WhatsAppTip from '../shared/WhatsAppTip';
import { listTips } from '../shared/whatsappTips';
import SectionDescription from '../shared/SectionDescription';
import { ListsList } from './ListsList';
import ListItems from './ListItems';
import AddList from './AddList';
import AddListItem from './AddListItem';
import ListsCategories, { ListsCategoriesActions } from './categories/ListsCategories';

const SUB_PATHS = [LISTS_TAB, 'categories'];

const SECTION_INFO = [
  { titleKey: 'lists_all_section_title',    textKey: 'lists_all_section_text' },
  { titleKey: 'lists_categories_section_title', textKey: 'lists_categories_section_text' },
];

// screen: 'list' | 'items' | 'new-list' | 'add'
const ListsActions = ({ screen, setScreen }) => (
  <div className="action-buttons">
    <ListButton
      isActive={screen === 'list' || screen === 'items'}
      onClick={() => setScreen(screen === 'add' ? 'items' : 'list')}
    />
    <AddButton
      isActive={screen === 'new-list' || screen === 'add'}
      onClick={() => setScreen(screen === 'items' ? 'add' : 'new-list')}
    />
  </div>
);

const ListsHub = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { setSubNav } = useMobileSubNav();
  const { open, close } = useChildNav();
  // Categories tab: plain list/add, same as other hubs.
  const categories = useHubScreen();

  // Lists tab: screen and list id come from the URL (see listsPath.js).
  const { subTab, screen, listId } = parseListsPath(pathname);
  const activeSubTab = Math.max(0, SUB_PATHS.indexOf(subTab));
  const isListsTab = subTab === LISTS_TAB;
  const base = `/lists/${subTab}`;

  const [list,       setList]       = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [tip,        setTip]        = useState(null); // { subTab, title, tips }

  const subTabs = [t('lists_all'), t('lists_categories')];
  const handleSubTabClick = (index) => navigate(`/lists/${SUB_PATHS[index]}`);

  useEffect(() => {
    setSubNav({ tabs: subTabs, activeTab: activeSubTab, onTabClicked: handleSubTabClick });
    return () => setSubNav(null);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSubTab, setSubNav]);

  // The open list, always loaded by id. Not taken from the list click's
  // history state: the browser replays that on refresh, so it can be older
  // than what's saved (a project linked, the name or closing date changed on
  // this page), and the edit forms start from it. Unknown id → all lists.
  useEffect(() => {
    if (!isListsTab || !listId || list?._id === listId) return;
    Fetch(`/api/data/lists.lists?search_fields=_id&search_values=${listId}`, 'GET', null,
      (data) => {
        const found = (data || [])[0];
        if (found) setList(found);
        else navigate(base, { replace: true });
      },
      () => navigate(base, { replace: true })
    );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isListsTab, listId]);

  // Moving between screens: to a parent (e.g. items → all lists) pops back,
  // to a child (all lists → a list → add item) opens a new history entry.
  const setScreen = (next) => {
    const target = listsPathFor(subTab, next, listId);
    if (target === pathname) return;
    if (pathname.startsWith(target + '/')) close(target);
    else open(target, list ? { list } : undefined);
  };

  const buildListTip = (list, title) => {
    if (!list?.lot_number) return null;
    const code = formatListCode(list, t);
    return { title: title || `✅ ${code} ${t('wat_list_created')}`, tips: listTips(list, code, t) };
  };

  const onListClick = (clicked) => { setList(clicked); open(listsPathFor(subTab, 'items', clicked._id), { list: clicked }); };
  const onArchived  = () => { setRefreshKey(k => k + 1); close(base); };
  const onDefaultSet = (defaultList) => {
    const code = formatListCode(defaultList, t);
    setTip({ subTab, ...buildListTip({ ...defaultList, is_default: true }, `★ ${t('wat_list_default_set')}: ${code}`) });
  };
  const onListCreated = (created) => {
    setRefreshKey(k => k + 1);
    const newTip = buildListTip(created);
    setTip(newTip ? { subTab, ...newTip } : null);
    close(base);
  };

  const actions = <ListsActions screen={screen} setScreen={setScreen} />;

  return (
    <div className="hub">
      <div className="hub-content">
        <SectionDescription
          title={t(SECTION_INFO[activeSubTab].titleKey)}
          text={t(SECTION_INFO[activeSubTab].textKey)}
        />

        {/* ── Lists tab ── */}
        {isListsTab && screen === 'list' && (
          <>
            {tip?.subTab === subTab && <WhatsAppTip title={tip.title} tips={tip.tips} onDismiss={() => setTip(null)} />}
            <ListsList
              key={subTab}
              onListClick={onListClick}
              refreshKey={refreshKey}
              onDefaultSet={onDefaultSet}
              actions={actions}
            />
            <FAB onClick={() => setScreen('new-list')} />
          </>
        )}
        {isListsTab && screen === 'items' && list?._id === listId && (
          <>
            <ListItems
              list={list}
              onArchived={onArchived}
              onBack={() => setScreen('list')}
              onListChange={setList}
              actions={actions}
            />
            <FAB onClick={() => setScreen('add')} />
          </>
        )}
        {isListsTab && (screen === 'new-list' || screen === 'add') && (
          <div className="content-header">{actions}</div>
        )}
        {isListsTab && screen === 'new-list' && (
          <AddList onBack={() => setScreen('list')} onSuccess={onListCreated} />
        )}
        {isListsTab && screen === 'add' && (
          <AddListItem defaultListId={listId} onBack={() => setScreen('items')} onSuccess={() => setScreen('items')} />
        )}

        {/* ── Categories tab ── */}
        {activeSubTab === 1 && (
          <ListsCategories
            screen={categories.screen}
            setScreen={categories.setScreen}
            actions={<ListsCategoriesActions screen={categories.screen} setScreen={categories.setScreen} />}
          />
        )}
      </div>
    </div>
  );
};

export default ListsHub;
